import api, { setAuthToken, setSelectedClinicaId } from './api';
import type {
  ParceiroDashboardResponse,
  ParceiroExamInfoResponse,
  ParceiroUploadResponse,
  ParceiroPendenciasQueryDto,
  ParceiroLoginDto,
  ParceiroLoginResponse,
  ParceiroSolicitarAcessoDto,
  ParceiroSolicitarAcessoResponse,
  ParceiroValidarTokenAcessoResponse,
  ParceiroDefinirSenhaDto,
  ParceiroAlterarSenhaDto,
  ParceiroClinicaPerfilContract,
  ParceiroContatosCadastradosResponse,
  ParceiroSolicitarAcessoDirecionadoDto,
  ParceiroSolicitarContatoAtivoDto,
  ParceiroSolicitarContatoAtivoResponse,
  MedicoParceiroLoginDto,
  MedicoParceiroLoginResponse,
  MedicoCertificadoUploadDto,
  MedicoCertificadoStatusResponse,
  MedicoAtendimentoPendenteContract,
  MedicoRealizarAtendimentoDto,
  MedicoRealizarAtendimentoResponse,
  ParceiroRecepcaoItemContract,
  MarcarExameRealizadoDto,
  ParceiroDarEntradaDto,
  ParceiroDarEntradaResponse,
  MedicoValidarTokenResetResponse,
  MedicoAtendimentoDetalhesContract,
  MedicoPerfilParceiroContract,
  MedicoClinicaVinculadaContract,
  MedicoAtendimentoForcarAsoResponseContract,
  MedicoAtendimentoRecuperarAsosResponseContract,
} from '@sstexames/shared-types';

export const clinicaService = {
  /**
   * Realiza login seguro da clínica credenciada via CNPJ e Senha
   */
  async login(dto: ParceiroLoginDto): Promise<ParceiroLoginResponse> {
    const response = await api.post<ParceiroLoginResponse>('/auth/login', dto);
    if (response.data?.token) {
      setAuthToken(response.data.token);
    }
    return response.data;
  },

  /**
   * Solicita primeiro acesso ou recuperação de senha enviando link aos e-mails cadastrados
   */
  async solicitarAcesso(dto: ParceiroSolicitarAcessoDto): Promise<ParceiroSolicitarAcessoResponse> {
    const response = await api.post<ParceiroSolicitarAcessoResponse>('/auth/solicitar-acesso', dto);
    return response.data;
  },

  /**
   * Consulta os contatos de e-mail cadastrados (mascarados) para o CNPJ
   */
  async getContatosCadastrados(cnpj: string): Promise<ParceiroContatosCadastradosResponse> {
    const response = await api.get<ParceiroContatosCadastradosResponse>('/auth/contatos-cadastrados', {
      params: { cnpj },
    });
    return response.data;
  },

  /**
   * Reenvia link de acesso para um e-mail específico cadastrado
   */
  async solicitarAcessoDirecionado(
    dto: ParceiroSolicitarAcessoDirecionadoDto,
  ): Promise<{ success: boolean; message: string; email_mascarado: string }> {
    const response = await api.post<{ success: boolean; message: string; email_mascarado: string }>(
      '/auth/solicitar-acesso-direcionado',
      dto,
    );
    return response.data;
  },

  /**
   * Solicita contato ativo pelo time SST Exames
   */
  async solicitarContatoAtivo(
    dto: ParceiroSolicitarContatoAtivoDto,
  ): Promise<ParceiroSolicitarContatoAtivoResponse> {
    const response = await api.post<ParceiroSolicitarContatoAtivoResponse>(
      '/auth/solicitar-contato-ativo',
      dto,
    );
    return response.data;
  },

  /**
   * Valida o token de primeiro acesso recebido por e-mail
   */
  async validarTokenAcesso(token: string): Promise<ParceiroValidarTokenAcessoResponse> {
    const response = await api.get<ParceiroValidarTokenAcessoResponse>('/auth/validar-token-acesso', {
      params: { token },
    });
    return response.data;
  },

  /**
   * Define uma nova senha para a clínica credenciada utilizando o token do e-mail
   */
  async definirSenha(dto: ParceiroDefinirSenhaDto): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/auth/definir-senha', dto);
    return response.data;
  },

  /**
   * Altera a senha da clínica credenciada a partir de uma sessão ativa
   */
  async alterarSenha(dto: ParceiroAlterarSenhaDto): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/auth/alterar-senha', dto);
    return response.data;
  },

  /**
   * Retorna os dados da clínica credenciada autenticada na sessão atual ou médico
   */
  async getMe(): Promise<
    | ParceiroClinicaPerfilContract
    | { role: 'medico'; medico: MedicoPerfilParceiroContract; clinicas: MedicoClinicaVinculadaContract[] }
  > {
    const response = await api.get('/me');
    return response.data;
  },

  /**
   * Retorna os dados do médico autenticado e suas clínicas vinculadas
   */
  async getMeMedico(): Promise<{
    role: 'medico';
    medico: MedicoPerfilParceiroContract;
    clinicas: MedicoClinicaVinculadaContract[];
  }> {
    const response = await api.get('/auth/medico/me');
    return response.data;
  },

  /**
   * Encerra a sessão da clínica ou médico
   */
  logout(): void {
    setAuthToken(null);
    setSelectedClinicaId(null);
    localStorage.removeItem('clinics_tipo_acesso');
  },

  /**
   * Obtém pendências de uma clínica pelo seu ID
   */
  async getPendencias(
    clinicaId: number,
    params?: ParceiroPendenciasQueryDto,
  ): Promise<ParceiroDashboardResponse> {
    const response = await api.get<ParceiroDashboardResponse>(
      `/pendencias/${clinicaId}`,
      { params },
    );
    return response.data;
  },

  /**
   * Define a data prevista de entrega de resultados de exames para alinhamento de SLA
   */
  async definirPrevisaoResultado(
    agendamento_ids: number[],
    data_previsao: string,
  ): Promise<{ success: boolean; message: string; updated_count: number }> {
    const response = await api.patch<{ success: boolean; message: string; updated_count: number }>(
      '/previsao-resultado',
      { agendamento_ids, data_previsao },
    );
    return response.data;
  },

  /**
   * Obtém os dados do agendamento a partir do token da clínica parceira
   */
  async getExameInfo(token: string): Promise<ParceiroExamInfoResponse> {
    const response = await api.get<ParceiroExamInfoResponse>(
      `/info/${encodeURIComponent(token.trim())}`,
    );
    return response.data;
  },

  /**
   * Envia o parecer clínico de aptidão e faz upload do laudo / ASO assinado em PDF
   */
  async uploadResultado(formData: FormData): Promise<ParceiroUploadResponse> {
    const response = await api.post<ParceiroUploadResponse>(
      '/upload-resultado',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  },

  /**
   * Faz upload de arquivo de laudo PDF diretamente na linha do exame
   */
  async uploadLaudoExame(
    agendamentoId: number,
    file: File,
    observacao?: string,
    valor?: string,
    agendamentoItemId?: number,
  ): Promise<ParceiroUploadResponse> {
    const formData = new FormData();
    formData.append('agendamento_id', String(agendamentoId));
    if (agendamentoItemId) {
      formData.append('agendamento_item_id', String(agendamentoItemId));
    }
    formData.append('file', file);
    if (observacao) formData.append('observacao', observacao);
    if (valor) formData.append('valor', valor);

    const response = await api.post<ParceiroUploadResponse>(
      '/upload-laudo-exame',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  },

  /**
   * Realiza login seguro do médico parceiro via E-mail e Senha (com suporte a 2FA)
   */
  async loginMedico(dto: MedicoParceiroLoginDto): Promise<MedicoParceiroLoginResponse> {
    const response = await api.post<MedicoParceiroLoginResponse>('/auth/medico/login', dto);
    if (response.data?.token) {
      setAuthToken(response.data.token);
    }
    return response.data;
  },

  /**
   * Solicita redefinição de senha para o médico
   */
  async forgotPasswordMedico(email: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/auth/medico/forgot-password', { email });
    return response.data;
  },

  /**
   * Redefine senha do médico com token enviado por e-mail
   */
  async resetPasswordMedico(dto: { token: string; email: string; nova_senha: string }): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/auth/medico/reset-password', dto);
    return response.data;
  },

  /**
   * Valida se o token de redefinição de senha do médico é válido
   */
  async validarTokenResetMedico(token: string, email: string): Promise<MedicoValidarTokenResetResponse> {
    const response = await api.get<MedicoValidarTokenResetResponse>('/auth/medico/validar-token', {
      params: { token, email },
    });
    return response.data;
  },

  /**
   * Consulta o status do certificado digital A1 do médico logado
   */
  async getCertificadoStatus(): Promise<MedicoCertificadoStatusResponse> {
    const response = await api.get<MedicoCertificadoStatusResponse>('/medico/certificado/status');
    return response.data;
  },

  /**
   * Upload e vinculação obrigatória do Certificado Digital A1 (.pfx) do médico
   */
  async uploadCertificado(dto: MedicoCertificadoUploadDto): Promise<MedicoCertificadoStatusResponse> {
    const response = await api.post<MedicoCertificadoStatusResponse>('/medico/certificado/upload', dto);
    return response.data;
  },

  /**
   * Reabre um atendimento finalizado pelo médico
   */
  async reabrirAtendimento(agendamentoId: number, motivo: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(`/atendimentos/${agendamentoId}/reabrir`, { motivo });
    return response.data;
  },

  /**
   * Força a emissão e assinatura digital do ASO caso tenha havido falha
   */
  async forcarGerarAso(agendamentoId: number): Promise<MedicoAtendimentoForcarAsoResponseContract> {
    const response = await api.post<MedicoAtendimentoForcarAsoResponseContract>(`/atendimentos/${agendamentoId}/forcar-aso`);
    return response.data;
  },

  /**
   * Dispara a rotina de recuperação automática de ASOs pendentes
   */
  async recuperarAsosPendentes(): Promise<MedicoAtendimentoRecuperarAsosResponseContract> {
    const response = await api.post<MedicoAtendimentoRecuperarAsosResponseContract>('/atendimentos/recuperar-asos-pendentes');
    return response.data;
  },

  /**
   * Lista os atendimentos clínicos pendentes para a clínica selecionada
   */
  async getAtendimentos(params?: Record<string, unknown>): Promise<{ data: MedicoAtendimentoPendenteContract[]; total: number; counts?: { fila: number; aguardando_exames: number; aguardando_laudo: number; realizados: number } }> {
    const response = await api.get<{ data: MedicoAtendimentoPendenteContract[]; total: number; counts?: { fila: number; aguardando_exames: number; aguardando_laudo: number; realizados: number } }>('/atendimentos', { params });
    return response.data;
  },

  /**
   * Obtém detalhes do agendamento, colaborador, exames previstos e questões da ficha de anamnese
   */
  async getAtendimentoDetalhes(id: number, clinica_id?: number): Promise<MedicoAtendimentoDetalhesContract> {
    const response = await api.get<MedicoAtendimentoDetalhesContract>(`/atendimentos/${id}`, {
      params: clinica_id ? { clinica_id } : undefined,
    });
    return response.data;
  },

  /**
   * Conclui o atendimento clínico e assina digitalmente o ASO com o Certificado A1 do médico
   */
  async realizarAtendimento(dto: MedicoRealizarAtendimentoDto): Promise<MedicoRealizarAtendimentoResponse> {
    const response = await api.post<MedicoRealizarAtendimentoResponse>('/atendimentos/realizar', dto);
    return response.data;
  },

  /**
   * Lista os agendamentos do dia na recepção da clínica credenciada
   */
  async getRecepcaoHoje(params?: {
    data?: string;
    data_fim?: string;
    search?: string;
    clinica_id?: number;
    modo?: 'entrada' | 'concluidos';
  }): Promise<ParceiroRecepcaoItemContract[]> {
    const response = await api.get<ParceiroRecepcaoItemContract[]>('/recepcao/hoje', { params });
    return response.data;
  },

  /**
   * Registra a entrada do paciente na recepção, liberando-o para o médico
   */
  async darEntradaRecepcao(
    agendamentoId: number,
    clinica_id?: number,
    dto?: ParceiroDarEntradaDto,
  ): Promise<ParceiroDarEntradaResponse> {
    const response = await api.post<ParceiroDarEntradaResponse>(
      `/recepcao/dar-entrada/${agendamentoId}`,
      dto || {},
      { params: clinica_id ? { clinica_id } : undefined },
    );
    return response.data;
  },

  /**
   * Marca um exame individual como realizado ou pendente na recepção
   */
  async marcarExameRealizado(
    dto: MarcarExameRealizadoDto,
    clinica_id?: number,
  ): Promise<{ success: boolean; message: string; agendamento_id: number; realizado: boolean }> {
    const response = await api.post<{ success: boolean; message: string; agendamento_id: number; realizado: boolean }>(
      '/recepcao/marcar-exame-realizado',
      dto,
      { params: clinica_id ? { clinica_id } : undefined },
    );
    return response.data;
  },

  /**
   * Baixa o arquivo do laudo autenticado em formato Blob
   * Servido diretamente pela API através de streaming binário (sem URLs externas)
   */
  async downloadLaudo(resultadoId: number): Promise<Blob> {
    const response = await api.get(`/download/${resultadoId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Baixa o arquivo do ASO autenticado em formato Blob
   * Servido diretamente pela API através de streaming binário (sem URLs externas)
   */
  async downloadAso(agendamentoId: number): Promise<Blob> {
    const response = await api.get(`/atendimentos/${agendamentoId}/aso/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Baixa o laudo anexado ao agendamento em formato Blob
   * Servido diretamente pela API através de streaming binário (sem URLs externas)
   */
  async downloadLaudoPorAgendamento(agendamentoId: number): Promise<Blob> {
    const response = await api.get(`/atendimentos/${agendamentoId}/laudo/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Baixa a imagem da biometria facial do atendimento em formato Blob
   * Servido diretamente pela API através de streaming binário autenticado
   */
  async downloadBiometria(agendamentoId: number): Promise<Blob> {
    const response = await api.get(`/atendimentos/${agendamentoId}/biometria`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Baixa o Kit de Exames (Guia+Ficha+ASO) em formato Blob
   * @param codigo - Código codificado em base64 (pedidoId_colaboradorId)
   */
  async downloadKitExame(codigo: string): Promise<Blob> {
    const response = await api.get(`/kit-exame/${codigo}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Ajusta a data de um agendamento futuro
   */
  async ajustarDataAgendamento(agendamentoId: number, data: { data: string; motivo: string }) {
    const response = await api.patch(`/atendimentos/${agendamentoId}/data`, data);
    return response.data;
  },

  /**
   * Permite que a recepção ou clínica registre falta (no-show) ou realização do agendamento
   */
  async atualizarStatusAgendamento(
    agendamentoId: number,
    status: 'realizado' | 'faltou',
    observacao?: string,
    data_previsao_resultado?: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      `/agendamento/${agendamentoId}/status`,
      { status, observacao, data_previsao_resultado },
    );
    return response.data;
  },
};
